/*
 * Ammonite System – dealer locator core
 * Shared by the /pages/dealers map section and the product-page "nearest dealers" block.
 * No dependencies. Exposes window.AmmoniteDealers.
 */
(function () {
  if (window.AmmoniteDealers) return;

  var LOC_KEY = 'ammonite:dealer-location';
  var GEO_CACHE_KEY = 'ammonite:geocode-cache';
  var LOC_TTL = 1000 * 60 * 60 * 24 * 30; // remember a visitor's location for 30 days
  var dataPromise = null;

  function storageGet(key) {
    try { return JSON.parse(window.localStorage.getItem(key)); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
  }

  /* Loads the dealer list once per page view. */
  function load(url) {
    if (!dataPromise) {
      dataPromise = fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('Dealer list request failed (' + r.status + ')');
          return r.text();
        })
        .then(function (txt) {
          return JSON.parse(txt).map(function (d) {
            d.lat = parseFloat(d.lat);
            d.lng = parseFloat(d.lng);
            return d;
          }).filter(function (d) { return isFinite(d.lat) && isFinite(d.lng); });
        })
        .catch(function (err) { dataPromise = null; throw err; });
    }
    return dataPromise;
  }

  /* Great-circle distance in km. */
  function distanceKm(lat1, lng1, lat2, lng2) {
    var R = 6371, rad = Math.PI / 180;
    var dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  /* Returns a new array sorted by distance, each item with a .km property. */
  function sortByDistance(dealers, lat, lng) {
    return dealers.map(function (d) {
      var copy = Object.assign({}, d);
      copy.km = distanceKm(lat, lng, d.lat, d.lng);
      return copy;
    }).sort(function (a, b) { return a.km - b.km; });
  }

  function formatDistance(km, unit) {
    if (unit === 'mi') {
      var mi = km * 0.621371;
      return (mi < 10 ? mi.toFixed(1) : Math.round(mi)) + ' mi';
    }
    return (km < 10 ? km.toFixed(1) : Math.round(km)) + ' km';
  }

  /*
   * Turns a postcode or place name into coordinates with OpenStreetMap Nominatim.
   * Tries the visitor's own country first (so "1234" or "Leuze" resolve locally),
   * then worldwide. Results are cached in localStorage to keep requests to a minimum.
   */
  function geocode(query, countryCode) {
    var q = (query || '').trim();
    if (!q) return Promise.reject(new Error('empty'));
    var cacheKey = (countryCode || '') + '|' + q.toLowerCase();
    var cache = storageGet(GEO_CACHE_KEY) || {};
    if (cache[cacheKey]) return Promise.resolve(cache[cacheKey]);

    function ask(cc) {
      var params = new URLSearchParams({ q: q, format: 'jsonv2', limit: '1', addressdetails: '0' });
      if (cc) params.set('countrycodes', cc);
      return fetch('https://nominatim.openstreetmap.org/search?' + params.toString(), {
        headers: { 'Accept-Language': document.documentElement.lang || 'en' }
      }).then(function (r) {
        if (!r.ok) throw new Error('Geocoding failed (' + r.status + ')');
        return r.json();
      }).then(function (list) { return list && list[0] ? list[0] : null; });
    }

    var attempt = countryCode ? ask(countryCode).then(function (hit) { return hit || ask(''); }) : ask('');
    return attempt.then(function (hit) {
      if (!hit) throw new Error('not-found');
      var result = {
        lat: parseFloat(hit.lat),
        lng: parseFloat(hit.lon),
        label: (hit.display_name || q).split(',').slice(0, 2).join(',').trim()
      };
      cache[cacheKey] = result;
      var keys = Object.keys(cache);
      if (keys.length > 50) delete cache[keys[0]];
      storageSet(GEO_CACHE_KEY, cache);
      return result;
    });
  }

  /* Browser geolocation wrapped in a promise. */
  function locate() {
    return new Promise(function (resolve, reject) {
      if (!('geolocation' in navigator)) return reject(new Error('unsupported'));
      navigator.geolocation.getCurrentPosition(
        function (pos) { resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: '' }); },
        function (err) { reject(err); },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    });
  }

  function saveLocation(loc) {
    storageSet(LOC_KEY, { lat: loc.lat, lng: loc.lng, label: loc.label || '', ts: Date.now() });
  }
  function savedLocation() {
    var loc = storageGet(LOC_KEY);
    if (!loc || !isFinite(loc.lat) || !isFinite(loc.lng)) return null;
    if (Date.now() - (loc.ts || 0) > LOC_TTL) return null;
    return loc;
  }
  function clearLocation() {
    try { window.localStorage.removeItem(LOC_KEY); } catch (e) { /* ignore */ }
  }

  /* Small helpers for building markup safely. */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function addressLine(d) {
    var place = [d.z, d.c].filter(Boolean).join(' ');
    return [d.a, place, d.k].filter(Boolean).join(', ');
  }
  function prettyUrl(u) {
    return String(u || '').replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
  }
  function directionsUrl(d) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' + d.lat + ',' + d.lng;
  }

  window.AmmoniteDealers = {
    load: load,
    distanceKm: distanceKm,
    sortByDistance: sortByDistance,
    formatDistance: formatDistance,
    geocode: geocode,
    locate: locate,
    saveLocation: saveLocation,
    savedLocation: savedLocation,
    clearLocation: clearLocation,
    esc: esc,
    addressLine: addressLine,
    prettyUrl: prettyUrl,
    directionsUrl: directionsUrl
  };
})();
